import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../UI/LoadingSpinner';

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState('soap');
  const [customTemplates, setCustomTemplates] = useState([]);

  // Clinically accurate pre-built templates
  const defaultTemplates = [
    {
      id: 'soap',
      name: 'SOAP',
      description: 'Subjective, Objective, Assessment, Plan',
      fields: ['Subjective', 'Objective', 'Assessment', 'Plan'],
      noteType: 'progress'
    },
    {
      id: 'dap', 
      name: 'DAP',
      description: 'Data, Assessment, Plan',
      fields: ['Data', 'Assessment', 'Plan'],
      noteType: 'progress'
    },
    {
      id: 'girp',
      name: 'GIRP', 
      description: 'Goals, Intervention, Response, Plan',
      fields: ['Goals', 'Intervention', 'Response', 'Plan'],
      noteType: 'progress'
    },
    {
      id: 'narrative',
      name: 'Narrative',
      description: 'Flowing clinical narrative format',
      fields: ['Clinical Summary'],
      noteType: 'progress'
    }
  ];

  useEffect(() => {
    const loadCustomTemplates = async () => {
      try {
        const saved = localStorage.getItem('chartara_custom_templates');
        if (saved) {
          setCustomTemplates(JSON.parse(saved));
        }
      } catch (error) {
        console.error('Failed to load custom templates:', error);
      }
    };

    loadCustomTemplates();
    
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';  
    return 'Good evening';
  };

  const handleStartRecording = () => {
    // Find the selected default template by id
    const defaultTemplate = defaultTemplates.find(t => t.id === selectedTemplate);
    console.log('Dashboard: Selected template ID:', selectedTemplate);
    console.log('Dashboard: Passing template to recorder:', defaultTemplate);
    navigate('/record', { state: { defaultTemplate: defaultTemplate } });
  };

  const handleCreateCustomTemplate = () => {
    navigate('/templates');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="h-full p-4 overflow-auto">
      <div className="max-w-5xl mx-auto">
        
        {/* Main Content - Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-8 h-full">
          
          {/* Left: Template Selection */}
          <div className="bg-white rounded-3xl shadow-2xl p-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-10 text-center">Select Note Format</h2>
            
            {/* Template Grid */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              {defaultTemplates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => setSelectedTemplate(template.id)}
                  className={`p-6 rounded-xl border-2 transition-all duration-200 text-center ${
                    selectedTemplate === template.id
                      ? 'border-blue-600 bg-blue-50 shadow-lg'
                      : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md'
                  }`}
                >
                  <div className="text-2xl font-bold text-gray-800 mb-3">{template.name}</div>
                  <div className="text-lg text-gray-600">{template.description}</div>
                </button>
              ))}
            </div>

            {/* Custom Templates */}
            {customTemplates.length > 0 && (
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Your Custom Templates</h3>
                <div className="grid grid-cols-2 gap-3">
                  {customTemplates.slice(0, 4).map((template) => (
                    <button
                      key={template.id}
                      onClick={() => setSelectedTemplate(template.id)}
                      className={`p-4 rounded-xl border-2 transition-all duration-200 text-center ${
                        selectedTemplate === template.id
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 bg-gray-50 hover:border-blue-300'
                      }`}
                    >
                      <div className="text-lg font-semibold text-gray-800">{template.name}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Create Custom Template */}
            <div className="text-center">
              <button
                onClick={handleCreateCustomTemplate}
                className="text-blue-600 hover:text-blue-700 font-semibold text-lg underline"
              >
                + Create Custom Template
              </button>
            </div>
          </div>

          {/* Right: Recording Action */}
          <div className="bg-white rounded-3xl shadow-2xl p-10 flex flex-col justify-center text-center">
            
            <div className="mb-8">
              <div className="w-32 h-32 bg-blue-600 rounded-3xl mx-auto mb-6 flex items-center justify-center">
                <div className="text-white text-6xl">🎤</div>
              </div>
              
              <h2 className="text-5xl font-bold text-gray-800 mb-6">Your AI Scribe is Ready</h2>
              
              <div className="bg-blue-50 rounded-2xl p-6 mb-8">
                <p className="text-xl text-gray-700 mb-2">
                  Using: <span className="font-bold text-blue-600">
                    {defaultTemplates.find(t => t.id === selectedTemplate)?.name || 
                     customTemplates.find(t => t.id === selectedTemplate)?.name}
                  </span>
                </p>
                <p className="text-gray-600">Speak naturally - AI handles the rest</p>
              </div>
            </div>
            
            <button
              onClick={handleStartRecording}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-3xl px-12 py-8 rounded-2xl transition-all duration-200 transform hover:scale-105 shadow-xl mx-auto"
            >
              START SPEAKING
            </button>
            
          </div>
        </div>

        {/* Bottom Stats Row */}
        <div className="grid grid-cols-2 gap-6 mt-12">
          <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-xl p-6 text-center">
            <div className="text-3xl font-bold text-white mb-1">3</div>
            <div className="text-blue-100 text-sm">Today's Notes</div>
          </div>
          <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-xl p-6 text-center">
            <div className="text-3xl font-bold text-white mb-1">18</div>
            <div className="text-blue-100 text-sm">This Week</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;